/**
 * First-meet banner and Connected-through visibility.
 *
 * Stored leftover via_person / at_location stay on the record. The UI hides
 * self-named Connected through and only shows the Met banner on a real
 * first-meet source that has a date or venue.
 */
import { formatMetDate } from './hwmDate';

export function contactDisplayNameForHwm(c) {
  if (!c) return '';
  const local = (c.local_display_name || '').trim();
  if (local) return local;
  const full = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return full || c.nickname || c.work?.company || c.organization || '';
}

function normPersonName(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function viewerIdentityNames(viewer) {
  if (!viewer) return [];
  const names = [];
  const full = `${viewer.first_name || ''} ${viewer.last_name || ''}`.trim();
  if (full) names.push(full);
  for (const value of [viewer.username, viewer.full_name]) {
    const v = String(value || '').trim();
    if (v) names.push(v);
  }
  return names;
}

function contactIdentityNames(c) {
  if (!c) return [];
  const names = [];
  const actual = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  if (actual) names.push(actual);
  for (const value of [c.local_display_name, c.canonical_name]) {
    const v = String(value || '').trim();
    if (v) names.push(v);
  }
  return names;
}

const FIRST_MEET_SOURCES = new Set([
  'introduction',
  'pik_consent',
  'share_link',
  'share_form',
  'share_submit_signup',
]);

export function isGroupShareContact(c) {
  return (c?.creation_context?.source || '') === 'group_share_session';
}

export function firstMeetBannerVisible(c, { isMyContact } = {}) {
  if (isMyContact || (c?.source || '') === 'self_card') return false;
  const hwm = c?.how_we_met && typeof c.how_we_met === 'object' ? c.how_we_met : {};
  const hasWhen = Boolean(String(hwm.date || '').trim());
  const hasWhere = Boolean(String(hwm.at_location || '').trim());
  if (!hasWhen && !hasWhere) return false;
  const source = String(c?.source || '');
  return isGroupShareContact(c) || FIRST_MEET_SOURCES.has(source);
}

export function connectedThroughPerson(c, viewer) {
  if ((c?.source || '') === 'self_card') return '';
  const hwm = c?.how_we_met;
  let name = '';
  if (hwm && typeof hwm === 'object' && hwm.via_person) {
    name = String(hwm.via_person).replace(/^\s*introduced by\s+/i, '').trim();
  }
  if (!name) return '';
  const n = normPersonName(name);
  for (const label of contactIdentityNames(c)) {
    if (normPersonName(label) === n) return '';
  }
  for (const self of viewerIdentityNames(viewer)) {
    if (normPersonName(self) === n) return '';
  }
  return name;
}

export function connectionComment(contact) {
  const hwm = contact?.how_we_met;
  if (!hwm) return '';
  if (typeof hwm === 'string') return hwm.trim();
  return String(hwm.description || '').trim();
}

export function firstMeetBannerText(c) {
  const hwm = c?.how_we_met && typeof c.how_we_met === 'object' ? c.how_we_met : {};
  const metDate = formatMetDate(hwm.date);
  const loc = (hwm.at_location || '').trim();
  return `Met ${metDate || 'recently'}${loc ? ` at ${loc}` : ''}`;
}
